import React, { useState, useEffect, useMemo } from 'react';
import { UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, Input } from 'reactstrap';
import 'bootstrap/dist/css/bootstrap.css';

import { useGlobalContext } from '../../../context';
import * as inventory_urls from '../../../urls/api-inventory/admin';
import * as shipment_urls from '../../../urls/api-shipments/admin';
import DispatchMarketsSelection from '../DispatchMarketsSelection';
import axios from 'axios';

const REGION_ORDER = ['California', 'Central Atlantic', 'Gulf Coast', 'Lower Atlantic', 'Midwest', 'New England', 'Northwest', 'Rocky Mountain'];
const ENTIRE_US_LABEL = 'Entire US';
const DEFAULT_COUNTRY_COORDINATES = { latitude: 39.8283, longitude: -98.5795 };
const REGION_NAME_ALIASES = {
  california: 'California',
  'california region': 'California',
  'central atlantic': 'Central Atlantic',
  'central atlantic region': 'Central Atlantic',
  'gulf coast': 'Gulf Coast',
  'gulf coast region': 'Gulf Coast',
  'lower atlantic': 'Lower Atlantic',
  'lower atlantic region': 'Lower Atlantic',
  midwest: 'Midwest',
  'mid west': 'Midwest',
  'midwest region': 'Midwest',
  'new england': 'New England',
  'new england region': 'New England',
  northwest: 'Northwest',
  'north west': 'Northwest',
  'northwest region': 'Northwest',
  'rocky mountain': 'Rocky Mountain',
  'rocky mountains': 'Rocky Mountain',
  'rocky mountain region': 'Rocky Mountain'
};

const normalizeText = value =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const canonicalizeRegionName = regionName => {
  const normalizedRegionName = normalizeText(regionName);

  if (REGION_NAME_ALIASES[normalizedRegionName]) {
    return REGION_NAME_ALIASES[normalizedRegionName];
  }

  const matchedRegion = REGION_ORDER.find(region => normalizeText(region) === normalizedRegionName);
  return matchedRegion || regionName;
};

const buildFlatMarketOptions = markets => {
  return (markets || []).map(market => ({
    value: {
      id: market.market_id,
      idArray: [market.market_id],
      coordinates: {
        latitude: Array.isArray(market.center) ? market.center[0] : DEFAULT_COUNTRY_COORDINATES.latitude,
        longitude: Array.isArray(market.center) ? market.center[1] : DEFAULT_COUNTRY_COORDINATES.longitude
      },
      scopeType: 'market',
      depth: 1
    },
    label: market.market_name
  }));
};

const buildRegionHierarchy = superRegions => {
  const regionsByName = {};

  (superRegions || []).forEach(superRegion => {
    (superRegion.Regions || []).forEach(region => {
      const canonicalRegionName = canonicalizeRegionName(region.region_name);

      if (!REGION_ORDER.includes(canonicalRegionName)) {
        return;
      }

      if (!regionsByName[canonicalRegionName]) {
        regionsByName[canonicalRegionName] = {
          id: region.region_id,
          name: canonicalRegionName,
          marketIds: [],
          markets: [],
          latitudeTotal: 0,
          longitudeTotal: 0
        };
      }

      (region.Markets || []).forEach(market => {
        if (!market || !market.market_id) {
          return;
        }

        const latitude = Array.isArray(market.center) ? market.center[0] : DEFAULT_COUNTRY_COORDINATES.latitude;
        const longitude = Array.isArray(market.center) ? market.center[1] : DEFAULT_COUNTRY_COORDINATES.longitude;

        regionsByName[canonicalRegionName].marketIds.push(market.market_id);
        regionsByName[canonicalRegionName].markets.push({
          id: market.market_id,
          name: market.market_name,
          coordinates: { latitude, longitude }
        });
        regionsByName[canonicalRegionName].latitudeTotal += latitude;
        regionsByName[canonicalRegionName].longitudeTotal += longitude;
      });
    });
  });

  const regionDefinitions = REGION_ORDER.map(regionName => regionsByName[regionName])
    .filter(Boolean)
    .map(region => ({
      id: region.id,
      name: region.name,
      marketIds: Array.from(new Set(region.marketIds)),
      markets: region.markets.sort((left, right) => left.name.localeCompare(right.name)),
      coordinates: region.marketIds.length
        ? {
            latitude: region.latitudeTotal / region.marketIds.length,
            longitude: region.longitudeTotal / region.marketIds.length
          }
        : DEFAULT_COUNTRY_COORDINATES
    }));

  const allMarketIds = Array.from(new Set(regionDefinitions.flatMap(region => region.marketIds)));
  const options = [
    {
      value: {
        id: 'entire-us',
        idArray: allMarketIds,
        coordinates: DEFAULT_COUNTRY_COORDINATES,
        scopeType: 'country',
        depth: 0,
        regionName: ENTIRE_US_LABEL
      },
      label: ENTIRE_US_LABEL
    }
  ];

  regionDefinitions.forEach(region => {
    options.push({
      value: {
        id: region.id,
        idArray: region.marketIds,
        coordinates: region.coordinates,
        scopeType: 'region',
        depth: 1,
        regionName: region.name
      },
      label: region.name
    });

    region.markets.forEach(market => {
      options.push({
        value: {
          id: market.id,
          idArray: [market.id],
          coordinates: market.coordinates,
          scopeType: 'market',
          depth: 2,
          regionName: region.name
        },
        label: market.name
      });
    });
  });

  return options;
};

const MerchantSelections = ({ selectedStore, setSelectedStore }) => {
  const { marketId, marketIdArray, isSingleMarket } = useGlobalContext();
  const [marketOptions, setMarketOptions] = useState([]);
  const [stores, setStores] = useState([]);
  const [storeName, setStoreName] = useState('');
  const [isFilterStore, setIsFilterStore] = useState(false);
  const [filteredStoreForSelect, setFilteredStoreForSelect] = useState([]);

  const HASURA_ENDPOINT = inventory_urls.hasuraHost;
  const SHIPMENTS_HASURA_ENDPOINT = shipment_urls.hasuraHost;

  const activeMarketIds = useMemo(() => {
    if (!isSingleMarket && Array.isArray(marketIdArray) && marketIdArray.length > 0) {
      return marketIdArray;
    }

    return marketId ? [marketId] : [];
  }, [isSingleMarket, marketIdArray, marketId]);

  const storesQuery = useMemo(() => {
    const escapedMarketIds = activeMarketIds.map(activeId => `"${activeId}"`).join(', ');

    return `{
      Stores(where: {market_id: {_in: [${escapedMarketIds}]}}) {
        state
        phone
        latitude
        longitude
        name
        address_1
        address_2
        city
        country
        zip
        store_id
        market_id
        sells_alcohol
        direct_type
        itemized_price
        StoreOwnerships {
          firstname
          lastname
          email
          merchant_id
          role
        }
      }
    }`;
  }, [activeMarketIds]);

  useEffect(() => {
    axios({
      method: 'post',
      headers: { Authorization: 'Bearer ' + localStorage.getItem('access_token'), 'content-type': 'application/json' },
      url: SHIPMENTS_HASURA_ENDPOINT,
      data: {
        query: `{
          SuperRegions(order_by: {super_region_name: asc}) {
            super_region_name
            super_region_id
            Regions(order_by: {region_name: asc}) {
              region_name
              region_id
              Markets(order_by: {market_name: asc}) {
                market_name
                center
                market_id
              }
            }
          }
        }`
      }
    })
      .then(response => {
        const superRegions = response && response.data && response.data.data && Array.isArray(response.data.data.SuperRegions) ? response.data.data.SuperRegions : [];

        if (superRegions.length > 0) {
          setMarketOptions(buildRegionHierarchy(superRegions));
          return;
        }

        return axios({
          method: 'post',
          headers: { Authorization: 'Bearer ' + localStorage.getItem('access_token'), 'content-type': 'application/json' },
          url: SHIPMENTS_HASURA_ENDPOINT,
          data: {
            query: `{
              Markets(where: {deleted_at: {_is_null: true}}, order_by: {market_name: asc}) {
                market_name
                market_id
                center
              }
            }`
          }
        }).then(flatResponse => {
          const nextMarkets = flatResponse && flatResponse.data && flatResponse.data.data && Array.isArray(flatResponse.data.data.Markets) ? flatResponse.data.data.Markets : [];
          setMarketOptions(buildFlatMarketOptions(nextMarkets));
        });
      })
      .catch(function(error) {});
  }, []);

  useEffect(() => {
    if (activeMarketIds.length === 0) {
      setStores([]);
      setFilteredStoreForSelect([]);
      return;
    }

    axios({
      method: 'post',
      headers: { Authorization: 'Bearer ' + localStorage.getItem('access_token'), 'content-type': 'application/json' },
      url: HASURA_ENDPOINT,
      data: { query: storesQuery }
    })
      .then(response => {
        if (response) {
          setStores(response.data.data.Stores);
          setFilteredStoreForSelect(response.data.data.Stores);
        }
      })
      .catch(function(error) {});
  }, [HASURA_ENDPOINT, storesQuery, activeMarketIds]);

  useEffect(() => {
    setStoreName('');
    setIsFilterStore(false);

    if (selectedStore && !activeMarketIds.includes(selectedStore.market_id)) {
      setSelectedStore('');
    }
  }, [activeMarketIds, selectedStore, setSelectedStore]);

  const filterStore = e => {
    const keyword = e.target.value;

    if (keyword !== '') {
      const results = stores.filter(store => {
        return store.name.toLowerCase().startsWith(keyword.toLowerCase());
        // Use the toLowerCase() method to make it case-insensitive
      });

      setIsFilterStore(true);
      setFilteredStoreForSelect(results);
    } else {
      setFilteredStoreForSelect(stores);
      // If the text field is empty, show all users
    }

    setStoreName(keyword);
  };

  return (
    <div style={{ minWidth: '320px' }}>
      <div style={{ marginBottom: '12px' }}>
        <DispatchMarketsSelection memoizedOptions={marketOptions} regionCounts={{}} activeTabLabel="" />
      </div>
      <UncontrolledDropdown>
        {selectedStore ? (
          <DropdownToggle caret id="driver-dropdown">
            {selectedStore.name} ✅
          </DropdownToggle>
        ) : (
          <DropdownToggle caret id="driver-dropdown" style={{ color: 'red' }}>
            Select a store/merchant
          </DropdownToggle>
        )}

        <DropdownMenu>
          <Input type="text" value={storeName} onChange={filterStore} style={{ color: 'black', alignSelf: 'left' }} placeholder="Store Name" id="store-filter" />

          {filteredStoreForSelect.length > 0 && isFilterStore ? (
            filteredStoreForSelect.map((store, key) => {
              if (key < 10) {
                return (
                  <DropdownItem key={store.store_id || store.id || store.auth0_id || store.name || `filtered-store-${key}`} onClick={() => setSelectedStore(store)}>
                    <p style={{ color: 'black', alignSelf: 'left' }}>{store.name}</p>
                  </DropdownItem>
                );
              }

              return null;
            })
          ) : filteredStoreForSelect.length === 0 && !isFilterStore ? (
            stores.map((store, key) => {
              if (key < 10) {
                return (
                  <DropdownItem key={store.store_id || store.id || store.auth0_id || store.name || `store-${key}`} onClick={() => setSelectedStore(store)}>
                    <p style={{ color: 'black', alignSelf: 'left' }}>{store.name}</p>
                  </DropdownItem>
                );
              }

              return null;
            })
          ) : (
            <p style={{ color: 'red', margin: 10, marginLeft: 25 }}>No stores found</p>
          )}
        </DropdownMenu>
      </UncontrolledDropdown>
    </div>
  );
};

export default MerchantSelections;
